import { useState } from "react";
import { motion } from "framer-motion";
import {
  parsePhoneNumberFromString,
  type CountryCode,
} from "libphonenumber-js";
import ChatService from "../../../../front-services/ChatService";
import Utility from "../../logic/Utility";
import ChatCache from "../../../../utils/ChatCache";
import localization from "../../../../assets/localization";
import PhoneInput, { defaultPhoneCountry } from "./PhoneInput";

const chatService = new ChatService();
const chatCache = new ChatCache();

// Fields cascade in after the panel has sprung up.
const panelVariants = {
  hidden: { y: "110%" },
  show: {
    y: 0,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 32,
      staggerChildren: 0.055,
      delayChildren: 0.12,
    },
  },
  exit: { y: "110%", transition: { duration: 0.35, ease: [0.4, 0, 0.6, 1] } },
};

const fieldVariants = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { duration: 0.35, ease: [0.25, 1, 0.35, 1] } },
  exit: { opacity: 0 },
};

export default function HandoffForm({
  color,
  setIsHandoffFormFullScreen,
  threadId,
  assistantId,
  setMessages,
  setShowButton,
  language,
}: any) {
  const [loading, setLoading] = useState(false);
  const [country, setCountry] = useState<CountryCode>(defaultPhoneCountry);
  const [phone, setPhone] = useState("");
  const [phoneError, setPhoneError] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const t = localization.get(language).handoff_form;

  const handleFormSubmit = (event: any) => {
    event.preventDefault();

    // Validate the phone first: the prefix gives us the country, and
    // libphonenumber checks the number is actually valid for it.
    const parsed = parsePhoneNumberFromString(phone, { defaultCountry: country });
    if (!parsed || !parsed.isValid()) {
      setPhoneError(true);
      return;
    }
    const e164 = parsed.number; // E.164, e.g. +39333...

    setLoading(true);
    setSubmitError(false);
    const formData = new FormData(event.target);

    const firstName = ((formData.get("first-name") as string) ?? "").trim();
    const lastName = ((formData.get("last-name") as string) ?? "").trim();
    const email = ((formData.get("email") as string) ?? "").trim();

    const contact = {
      name: [firstName, lastName].filter(Boolean).join(" "),
      email,
      phone: e164,
    };

    chatService
      .handoff(assistantId, threadId, contact)
      .then((response) => {
        // A 401 without a token (older session) lands here: fail into the
        // existing generic error state instead of trying to parse a body.
        if (!response.ok) throw new Error("handoff failed");
        return response.json();
      })
      .then((data) => {
        if (!data?.success) throw new Error("handoff failed");

        setShowButton(false);
        setIsHandoffFormFullScreen(false);
        setLoading(false);
        const confirmationMessage = {
          type: "bot",
          content:
            data.mode === "email"
              ? t.handoff_email_success.replace("{email}", contact.email)
              : t.success,
          time: Utility.getTime(),
        };
        setMessages((prevMessages: any) => [
          ...prevMessages,
          confirmationMessage,
        ]);
        chatCache.addMessage(confirmationMessage, threadId, assistantId);
      })
      .catch(() => {
        setLoading(false);
        setSubmitError(true);
      });
  };

  const setError = (e: any, message: string) =>
    (e.target as HTMLInputElement).setCustomValidity(message);
  const clearError = (e: any) =>
    (e.target as HTMLInputElement).setCustomValidity("");

  return (
    <motion.div
      className="handoff-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, transition: { delay: 0.15, duration: 0.25 } }}
      onClick={() => setIsHandoffFormFullScreen(false)}
    >
      <motion.div
        className="handoff-form-container"
        variants={panelVariants}
        initial="hidden"
        animate="show"
        exit="exit"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="handoff-minimize-button"
          style={{ backgroundColor: color }}
          onClick={() => setIsHandoffFormFullScreen(false)}
          aria-label={t.close_aria_label}
        >
          <i className="fa-solid fa-x"></i>
        </button>

        <motion.div className="handoff-form-header" variants={fieldVariants}>
          <h2>{t.title}</h2>
          <p>{t.desc}</p>
        </motion.div>

        <form className="handoff-form-form" onSubmit={handleFormSubmit}>
          <motion.div className="handoff-form-row" variants={fieldVariants}>
            <div className="handoff-form-input">
              <label htmlFor="handoff-first-name">{t.firstname}</label>
              <input
                id="handoff-first-name"
                type="text"
                placeholder={t.firstname}
                name="first-name"
                required
                onInvalid={(e) => setError(e, t.firstname_error)}
                onInput={clearError}
              />
            </div>
            <div className="handoff-form-input">
              <label htmlFor="handoff-last-name">{t.lastname}</label>
              <input
                id="handoff-last-name"
                type="text"
                placeholder={t.lastname}
                name="last-name"
                required
                onInvalid={(e) => setError(e, t.lastname_error)}
                onInput={clearError}
              />
            </div>
          </motion.div>

          <motion.div className="handoff-form-input" variants={fieldVariants}>
            <label htmlFor="handoff-phone">{t.phone}</label>
            <PhoneInput
              inputId="handoff-phone"
              country={country}
              onCountryChange={setCountry}
              value={phone}
              onChange={(v) => {
                setPhone(v);
                if (phoneError) setPhoneError(false);
              }}
              language={language}
              placeholder={t.phone}
              searchPlaceholder={t.phone_search}
              invalid={phoneError}
            />
            {phoneError ? (
              <span className="handoff-form-error">{t.phone_error}</span>
            ) : (
              <span className="handoff-form-hint">{t.phone_hint}</span>
            )}
          </motion.div>

          <motion.div className="handoff-form-input" variants={fieldVariants}>
            <label htmlFor="handoff-email">{t.email}</label>
            <input
              id="handoff-email"
              type="email"
              placeholder={t.email}
              name="email"
              required
              onInvalid={(e) => setError(e, t.email_error)}
              onInput={clearError}
            />
          </motion.div>

          <motion.button
            type="submit"
            className="handoff-form-button"
            style={{ backgroundColor: color }}
            disabled={loading}
            variants={fieldVariants}
          >
            {loading ? (
              <i className="fa-solid fa-spinner fa-spin"></i>
            ) : (
              <>
                <i className="fa-solid fa-paper-plane"></i> {t.submit}
              </>
            )}
          </motion.button>

          {submitError && (
            <span className="handoff-form-error">{t.submit_error}</span>
          )}
        </form>
      </motion.div>
    </motion.div>
  );
}
